'use client';
import Image from '@/components/Image';
import SwiperNavigationButtons from '@/components/SwiperNavigation';
import logger from '@/utils/client-logger';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { Swiper as SwiperType } from 'swiper';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useTranslations } from 'next-intl';

interface SceneImageData {
  src: string;
  isTowImage: boolean;
  isThreeImage: boolean;
  index?: number;
}

interface GoodsSceneProps {
  sceneImg: string[];
}

const GoodsScene: React.FC<GoodsSceneProps> = ({ sceneImg }) => {
  const [sceneImgCompute, setSceneImgCompute] = useState<SceneImageData[]>([]);
  const [showSwiperImage, setShowSwiperImage] = useState(true);
  const swiperRef = useRef<SwiperType | null>(null);
  const t = useTranslations('goodsDetail');

  // 加载图片信息，保持顺序
  const loadImageInfo = useCallback(async () => {
    if (!Array.isArray(sceneImg) || sceneImg.length === 0) {
      return;
    }

    try {
      setShowSwiperImage(false);

      // 使用Promise.all保持顺序
      const _sceneImgCompute = await Promise.all(
        sceneImg.map((item, index) => {
          return new Promise<SceneImageData>((resolve) => {
            const img = new globalThis.Image();
            img.onload = () => {
              const ratio = img.width / img.height;
              const isTowImage = ratio > 0.9 && ratio < 2;
              const isThreeImage = ratio >= 2;

              resolve({
                src: item,
                isTowImage,
                isThreeImage,
                index, // 保留原始索引用于调试
              });
            };
            img.onerror = () => {
              // 图片加载失败时的默认值
              resolve({
                src: item,
                isTowImage: false,
                isThreeImage: false,
                index,
              });
            };
            img.src = item;
          });
        }),
      );

      // 更新数据
      setSceneImgCompute(_sceneImgCompute);

      // 显示轮播图
      setShowSwiperImage(true);

      // 确保swiper正确初始化
      setTimeout(() => {
        if (swiperRef.current) {
          swiperRef.current.update();
        }
      }, 100);
    } catch (error) {
      logger.error('图片信息加载失败:', error);
      // 降级处理
      setSceneImgCompute(
        sceneImg.map((item) => ({
          src: item,
          isTowImage: false,
          isThreeImage: false,
        })),
      );
      setShowSwiperImage(true);
    }
  }, [sceneImg]);

  useEffect(() => {
    if (Array.isArray(sceneImg) && sceneImg.length > 0) {
      loadImageInfo();
    }
  }, [sceneImg, loadImageInfo]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (swiperRef.current) {
        swiperRef.current.update();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  const handleSwiperInit = (swiper: SwiperType) => {
    swiperRef.current = swiper;
  };
  if (!showSwiperImage || !Array.isArray(sceneImg) || sceneImg.length === 0) {
    return null;
  }

  return (
    <div className="might-like goods-scene w-full box-border hover:[&_.might-like-next-scene]:!flex hover:[&_.might-like-prev-scene]:!flex mt-[60px] pb-[60px]">
      <p className="text-[32px] font-bb text-center mb-[32px] leading-[39px]">
        {t('designInspirationDetails')}
      </p>
      <div className="swiper-nav-container relative w-[95vw] mx-auto box-border">
        <Swiper
          modules={[Navigation]}
          observer
          observeParents
          observeSlideChildren
          slidesPerView="auto"
          spaceBetween={16}
          navigation={{
            nextEl: '.goods-scene-next',
            prevEl: '.goods-scene-prev',
          }}
          onSwiper={handleSwiperInit}
        >
          {sceneImgCompute.map((item, index) => {
            // 计算内联样式
            let width = '23.12vw';
            let minWidth = '292px';
            const height = '28.12vw';
            const minHeight = '356px';

            if (item.isTowImage) {
              width = 'calc(23.12vw * 2 + 16px)';
              minWidth = 'calc(292px * 2 + 16px)';
            }
            if (item.isThreeImage) {
              width = 'calc(23.12vw * 3 + 32px)';
              minWidth = 'calc(292px * 3 + 32px)';
            }

            const slideStyle = {
              width,
              minWidth,
              height,
              minHeight,
            };

            return (
              <SwiperSlide key={index} className="might-like-item" style={slideStyle}>
                <Image
                  src={item.src}
                  alt={`Scene ${index + 1}`}
                  width={1000}
                  height={600}
                  quality={100}
                  unoptimized
                  className="h-full w-full object-cover select-none block"
                />
              </SwiperSlide>
            );
          })}
        </Swiper>

        {/* Navigation Buttons */}
        <SwiperNavigationButtons
          navigationPrefix="goods-scene"
          prevButtonPosition="left-16"
          nextButtonPosition="right-16"
        />
      </div>
    </div>
  );
};

export default GoodsScene;
